import React from 'react'
import { useFormikContext } from 'formik'
import { BasicTextFieldProps } from '../BasicTextField'
import { UploadedFile } from '../../UploadedFile'
import { FileUploadInput } from '../../FileUploadInput'
import { FormControl, FormHelperText, Typography } from '@mui/material'
import tw from 'twin.macro'
import { RiskMinor } from '@shopify/polaris-icons'
import { MAX_FILE_SIZE_BYTES } from 'src/constants/fileUpload'

const ACCESS_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'application/pdf']

const deriveUploadedFileRemoveTestId = (inputDataTestId?: string) =>
  inputDataTestId?.endsWith('-input')
    ? inputDataTestId.replace(/-input$/, '-remove-btn')
    : undefined

export const FormikFileInput = <T extends unknown>({
  name,
  inputDataTestId,
  uploadedFileRemoveDataTestId,
}: BasicTextFieldProps<T> & {
  inputDataTestId?: string
  uploadedFileRemoveDataTestId?: string
}) => {
  const removeTestId =
    uploadedFileRemoveDataTestId ?? deriveUploadedFileRemoveTestId(inputDataTestId)
  const { values, touched, errors, setFieldValue, setFieldTouched } = useFormikContext<T>()

  const isTouched = touched[name] as boolean
  const error = errors[name] as string | undefined

  return (
    <div>
      <FormControl fullWidth error={!!error}>
        {values[name] ? (
          <UploadedFile
            file={values[name] as any}
            onRemove={() => {
              setFieldTouched(name as string, false)
              setFieldValue(name as string, null)
            }}
            removeButtonTestId={removeTestId}
          />
        ) : (
          <FileUploadInput
            fileSizeLimit={MAX_FILE_SIZE_BYTES}
            acceptTypes={ACCESS_TYPES}
            inputDataTestId={inputDataTestId}
            highlightEmptyError={!values[name] && Boolean(error) && Boolean(isTouched)}
            onSelect={(file) => {
              setFieldValue(name as string, file)
            }}
          />
        )}
        {isTouched && error ? (
          <FormHelperText component="div" css={tw`ml-0`}>
            <div css={tw`flex items-center gap-1`}>
              <RiskMinor width={20} css={tw`fill-critical-500`} />
              <Typography variant="body2" css={tw`text-critical-500`} component="span">
                {error}
              </Typography>
            </div>
          </FormHelperText>
        ) : null}
      </FormControl>
    </div>
  )
}
